/**
 * Server functions for the editable "Standaard winkel" baseline.
 * Imports the DB loader from `standardStoreLoader.ts` for internal use
 * but DOES NOT re-export it — that would prevent Vite from tree-shaking
 * `db` out of the client bundle when the form hook imports
 * `getStandardStoreBaselineFn` from this module. Same pattern as
 * `storeSizeBucketsServer.ts`.
 */
import { createServerFn } from "@tanstack/react-start"
import { getRequest } from "@tanstack/react-start/server"
import type { StoreSizeBucket } from "@/features/reports/core/storeSize"
import { db } from "@/db/db"
import { standardStoreBaseline } from "@/db/schema"
import { logAuditEvent } from "@/features/audit-logs/server/auditLogger"
import {
  standardStoreBaselinePayloadSchema,
  type StandardStoreBaselinePayload,
} from "@/features/standard-store/core/standardStoreSchema"
import {
  loadActiveBandwidthsForBaseline,
  type ActiveBandwidth,
} from "@/features/standard-store/server/activeBandwidthsLoader"
import {
  loadStandardStoreBaseline,
  STANDARD_STORE_SINGLETON_ID,
} from "@/features/standard-store/server/standardStoreLoader"
import { loadStoreSizeBuckets } from "@/features/store-size-buckets/server/storeSizeBucketsLoader"
import { assertPermission } from "@/lib/assert-permission.server"
import { authFunctionMiddleware } from "@/lib/auth-middleware"

export type StandardStoreBaselineDto = {
  gondolas: number
  walls: number
  racks: number
  metersPerMg: Record<string, number>
}

export type StandardStorePageData = {
  baseline: StandardStoreBaselineDto
  /** Active configuration's bandwidth codes — defines which rows the form renders, in `sortOrder`. */
  bandwidths: ActiveBandwidth[]
  /** Rack-count thresholds — used to derive the read-only Store Size label from `racks`. */
  sizeBuckets: StoreSizeBucket[]
}

function toDto(payload: StandardStoreBaselinePayload): StandardStoreBaselineDto {
  return {
    gondolas: payload.gondolas,
    walls: payload.walls,
    racks: payload.racks,
    metersPerMg: payload.metersPerMg,
  }
}

export const getStandardStoreBaselineFn = createServerFn({ method: "GET" })
  .middleware([authFunctionMiddleware])
  .handler(async (): Promise<StandardStoreBaselineDto> => {
    const request = getRequest()
    await assertPermission(request, "standardStore", "read")
    const baseline = await loadStandardStoreBaseline()
    return {
      gondolas: baseline.meta.gondolas,
      walls: baseline.meta.walls,
      racks: baseline.meta.racks,
      metersPerMg: Object.fromEntries(baseline.metersPerMg),
    }
  })

/**
 * Combined fetch for the Settings → Standaard winkel form: baseline +
 * active bandwidth codes (for row labels + color swatches + ordering) +
 * size buckets (for the derived Store Size label). One round-trip
 * instead of three.
 */
export const getStandardStorePageDataFn = createServerFn({ method: "GET" })
  .middleware([authFunctionMiddleware])
  .handler(async (): Promise<StandardStorePageData> => {
    const request = getRequest()
    await assertPermission(request, "standardStore", "read")
    const [baseline, bandwidths, sizeBuckets] = await Promise.all([
      loadStandardStoreBaseline(),
      loadActiveBandwidthsForBaseline(),
      loadStoreSizeBuckets(),
    ])
    return {
      baseline: {
        gondolas: baseline.meta.gondolas,
        walls: baseline.meta.walls,
        racks: baseline.meta.racks,
        metersPerMg: Object.fromEntries(baseline.metersPerMg),
      },
      bandwidths,
      sizeBuckets,
    }
  })

export const saveStandardStoreBaselineFn = createServerFn({ method: "POST" })
  .middleware([authFunctionMiddleware])
  .inputValidator((data: unknown) => {
    const parsed = standardStoreBaselinePayloadSchema.safeParse(data)
    if (!parsed.success) {
      const message = parsed.error.issues
        .map((i) => i.message)
        .filter((m, idx, arr) => arr.indexOf(m) === idx)
        .join("; ")
      throw new Error(`Invalid Standard store baseline: ${message}`)
    }
    return parsed.data
  })
  .handler(async ({ data }): Promise<StandardStoreBaselineDto> => {
    const request = getRequest()
    const user = await assertPermission(request, "standardStore", "update")

    const before = await loadStandardStoreBaseline()

    await db.transaction(async (tx) => {
      await tx
        .insert(standardStoreBaseline)
        .values({
          id: STANDARD_STORE_SINGLETON_ID,
          gondolas: data.gondolas,
          walls: data.walls,
          racks: data.racks,
          metersPerMg: data.metersPerMg,
          lastModifiedBy: user.id,
        })
        .onConflictDoUpdate({
          target: standardStoreBaseline.id,
          set: {
            gondolas: data.gondolas,
            walls: data.walls,
            racks: data.racks,
            metersPerMg: data.metersPerMg,
            lastModified: new Date(),
            lastModifiedBy: user.id,
          },
        })
    })

    await logAuditEvent(
      {
        action: "standardStore.update",
        entityType: "standardStore",
        entityId: "global",
        entityName: "Standard store",
        summary: `Updated Standard store baseline`,
        metadata: {
          before: {
            ...before.meta,
            metersPerMg: Object.fromEntries(before.metersPerMg),
          },
          after: {
            gondolas: data.gondolas,
            walls: data.walls,
            racks: data.racks,
            metersPerMg: data.metersPerMg,
          },
        },
      },
      request,
    )

    return toDto(data)
  })
